import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "./_shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { TourVideoCard } from "@/components/TourVideo";
import { Search, BookOpen, ChevronRight, ArrowLeft, LifeBuoy, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { HELP_ARTICLES, HELP_CATEGORIES } from "@/data/helpArticles";

export default function Help() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("start");
  const [openArticle, setOpenArticle] = useState(null);

  // Field workers shouldn't be here at all — their nav doesn't show Help.
  // But if they manage to navigate here, send them somewhere useful.
  const isOffice = user?.role && user.role !== "field";

  const searchHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return HELP_ARTICLES.filter((a) =>
      a.title.toLowerCase().includes(q) ||
      a.blurb.toLowerCase().includes(q) ||
      (a.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [query]);

  const articlesInCategory = useMemo(
    () => HELP_ARTICLES.filter((a) => a.category === activeCategory),
    [activeCategory]
  );

  // Close the open article when user changes category / searches.
  useEffect(() => { setOpenArticle(null); }, [activeCategory, query]);

  if (!isOffice) {
    return (
      <div className="p-6">
        <p className="text-sm">The Help centre is only for office and admin users.</p>
      </div>
    );
  }

  const visibleArticles = searchHits ?? articlesInCategory;

  return (
    <div className="p-6 lg:p-10 max-w-[1200px] mx-auto" data-testid="help-page">
      <PageHeader
        tag="/ help centre"
        title="How-to guides"
        subtitle="Everything you need to run Fieldlink — written for civil-construction offices, not Silicon Valley."
        action={
          <Button asChild variant="outline" className="rounded-sm border-zinc-900 uppercase tracking-wider font-bold text-xs">
            <Link to="/app/support" data-testid="help-contact-support-btn">
              <LifeBuoy className="w-3.5 h-3.5 mr-2" /> Talk to a human
            </Link>
          </Button>
        }
      />

      <TourVideoCard />

      {/* Search */}
      <div className="hard-card p-4 mb-6 flex items-center gap-3">
        <Search className="w-4 h-4 text-zinc-400 shrink-0" />
        <Input
          placeholder="Search help… (e.g., 'invite team', 'pay run', 'sssp')"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="border-0 shadow-none focus-visible:ring-0 px-0 h-auto bg-transparent text-sm"
          data-testid="help-search-input"
        />
        {query && (
          <button onClick={() => setQuery("")} className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-[#FF5F15]" data-testid="help-search-clear">
            Clear
          </button>
        )}
      </div>

      {/* Layout: sidebar of categories + article list/reader */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        {/* Categories sidebar */}
        <aside className="lg:sticky lg:top-6 self-start" data-testid="help-categories">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2 px-2">Topics</div>
          <nav className="space-y-1">
            {HELP_CATEGORIES.map((c) => {
              const count = HELP_ARTICLES.filter((a) => a.category === c.id).length;
              const active = !searchHits && c.id === activeCategory;
              return (
                <button
                  key={c.id}
                  onClick={() => { setActiveCategory(c.id); setQuery(""); }}
                  className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                    active ? "bg-[#09090B] text-white" : "bg-transparent hover:bg-zinc-100 text-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                  }`}
                  data-testid={`help-cat-${c.id}`}>
                  <span className="flex items-center gap-2">
                    <span aria-hidden="true">{c.emoji}</span>
                    <span className="font-bold">{c.label}</span>
                  </span>
                  <span className={`text-[10px] font-mono tracking-widest ${active ? "text-zinc-400" : "text-zinc-400"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Article list / reader */}
        <section>
          {openArticle ? (
            <ArticleReader article={openArticle} onBack={() => setOpenArticle(null)} />
          ) : (
            <>
              {searchHits && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
                  {searchHits.length} result{searchHits.length === 1 ? "" : "s"} for &quot;{query}&quot;
                </div>
              )}
              {visibleArticles.length === 0 ? (
                <div className="hard-card p-8 text-center" data-testid="help-no-results">
                  <BookOpen className="w-8 h-8 mx-auto text-zinc-300 mb-3" />
                  <p className="text-sm text-zinc-500">Nothing matched. Try a broader search or open a category on the left.</p>
                  <div className="mt-4">
                    <Link to="/app/support" className="text-[#FF5F15] font-bold uppercase text-xs tracking-wider hover:underline">
                      <Mail className="w-3 h-3 inline mr-1" /> Email us your question
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {visibleArticles.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => setOpenArticle(a)}
                      className="hard-card p-4 text-left hover:border-[#FF5F15] hover:shadow-[4px_4px_0_0_#FF5F15] transition-all group"
                      data-testid={`help-article-${a.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-display font-bold uppercase tracking-tight text-base leading-snug group-hover:text-[#FF5F15] transition-colors">{a.title}</h3>
                        <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5 group-hover:text-[#FF5F15] transition-colors" />
                      </div>
                      <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">{a.blurb}</p>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function ArticleReader({ article, onBack }) {
  return (
    <article className="hard-card p-6 lg:p-8" data-testid={`help-reader-${article.id}`}>
      <button onClick={onBack} className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-[#FF5F15] mb-4 inline-flex items-center gap-1" data-testid="help-reader-back">
        <ArrowLeft className="w-3 h-3" /> Back to topics
      </button>
      <h1 className="font-display font-black uppercase tracking-tight text-3xl leading-none">{article.title}</h1>
      <p className="text-sm text-zinc-500 mt-2">{article.blurb}</p>
      <div className="mt-6 prose prose-sm max-w-none dark:prose-invert">
        {article.body}
      </div>
      <div className="mt-8 pt-6 border-t border-zinc-200 dark:border-zinc-800 flex items-center justify-between flex-wrap gap-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          Did this help?
        </div>
        <Link to="/app/support" className="text-xs font-bold uppercase tracking-wider text-[#FF5F15] hover:underline inline-flex items-center gap-1" data-testid="help-reader-contact">
          <LifeBuoy className="w-3.5 h-3.5" /> Still stuck — talk to support
        </Link>
      </div>
    </article>
  );
}
